import { z } from 'zod';
/** How long a message stays (ADR-042): a pointer, not a record. */
export const NOTIFICATION_DAYS = 90;
/**
 * What happens to an intake its responsibles are told of (ADR-056 §5), whoever or whatever did
 * it: the invitation accepted, a possible hit or no answer of the screening, a register document
 * arrived, everything decided but not completed, completed - or completed without its GwG record.
 */
export const IntakeNewsKind = z
    .enum([
    'accepted',
    'screening-hit',
    'screening-failed',
    'register-document',
    'ready',
    'closed',
    'record-failed',
    // A client of the intake is erased once its retention ends - 30 days ahead (ADR-063).
    'erasure-announced',
    // The client answered the contract's offer in the portal, or withdrew (ADR-072 §§ 4-5).
    'engagement-accepted',
    'engagement-declined',
    'engagement-revoked',
    // Notice of the contract, given or received (ADR-071 stage 6).
    'engagement-terminated',
])
    .meta({ id: 'IntakeNewsKind' });
/**
 * What a person of the firm is told about (ADR-042): made responsible for an intake by someone
 * else, the person of an intake they are responsible for sent their data, a colleague set them
 * a follow-up - and the news of their intakes. Each about an intake.
 */
export const NotificationKind = z
    .enum(['responsible', 'submitted', 'follow-up', ...IntakeNewsKind.options])
    .meta({ id: 'NotificationKind' });
/** One message to the asking person; the intake's name as it is now, never kept with it. */
export const Notification = z
    .object({
    id: z.uuid(),
    kind: NotificationKind,
    /** The intake it is about; `null` for a follow-up about no intake. */
    caseId: z.uuid().nullable(),
    /** The intake's name; `null` without one, or where it is gone. */
    caseName: z.string().nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    /** `null` while unread. */
    readAt: z.iso.datetime({ offset: true }).nullable(),
})
    .meta({ id: 'Notification' });
/** Newest first, at most a page; with how many of the person's are unread. */
export const NotificationList = z
    .object({ items: z.array(Notification), unread: z.number().int().nonnegative() })
    .meta({ id: 'NotificationList' });
export const NotificationQuery = z
    .object({
    /** `true`: the unread only. */
    unread: z.enum(['true', 'false']).optional(),
})
    .meta({ id: 'NotificationQuery' });
export const NotificationParams = z
    .object({ notificationId: z.uuid() })
    .meta({ id: 'NotificationParams' });
/** What a person may get by mail: the notices of the firm (administrators) and their own. */
export const MailedNotificationKind = z
    .enum(['notice', ...NotificationKind.options])
    .meta({ id: 'MailedNotificationKind' });
/**
 * Whether a person gets a kind by mail (ADR-042): notices as before for administrators unless
 * they turn them off; their own messages only if they turn them on.
 */
export const NotificationPreference = z
    .object({ kind: MailedNotificationKind, mail: z.boolean() })
    .meta({ id: 'NotificationPreference' });
export const NotificationPreferences = z
    .array(NotificationPreference)
    .meta({ id: 'NotificationPreferences' });
/**
 * What a message says - in the app above the intake's name, in a mail alone: never about whom,
 * so no data of a client leaves Kanos (ADR-017, ADR-042).
 */
export const notificationTitle = {
    responsible: 'Sie sind jetzt für eine Aufnahme verantwortlich',
    submitted: 'Neue Angaben aus dem Mandantenportal zu einer Ihrer Aufnahmen',
    'follow-up': 'Eine neue Wiedervorlage für Sie',
    accepted: 'Die Einladung zu einer Ihrer Aufnahmen wurde angenommen',
    'screening-hit': 'Möglicher Treffer im Abgleich zu einer Ihrer Aufnahmen',
    'screening-failed': 'Der Abgleich zu einer Ihrer Aufnahmen bekam keine Antwort',
    'register-document': 'Ein Registerauszug zu einer Ihrer Aufnahmen ist da',
    ready: 'Eine Ihrer Aufnahmen ist bereit zum Abschluss',
    closed: 'Eine Ihrer Aufnahmen ist abgeschlossen',
    'record-failed': 'Zu einer Ihrer abgeschlossenen Aufnahmen fehlt die GwG-Aufzeichnung',
    'erasure-announced': 'Ein Mandant Ihrer Aufnahmen wird nach Ablauf der Aufbewahrungsfrist gelöscht',
    'engagement-accepted': 'Ein Mandant hat das Angebot zum Steuerberatungsvertrag angenommen',
    'engagement-declined': 'Ein Mandant hat das Angebot zum Steuerberatungsvertrag abgelehnt',
    'engagement-revoked': 'Ein Mandant hat den Steuerberatungsvertrag widerrufen',
    'engagement-terminated': 'Ein Steuerberatungsvertrag Ihrer Mandanten wurde gekündigt',
};
/** Mail unless a person chose otherwise. */
export const MAIL_BY_DEFAULT = {
    notice: true,
    responsible: false,
    submitted: false,
    'follow-up': false,
    accepted: false,
    'screening-hit': false,
    'screening-failed': false,
    'register-document': false,
    ready: false,
    closed: false,
    'record-failed': false,
    'erasure-announced': false,
    'engagement-accepted': true,
    'engagement-declined': true,
    'engagement-revoked': true,
    'engagement-terminated': true,
};
//# sourceMappingURL=notifications.js.map